import React, { useState, useRef, useEffect } from 'react';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore'; // ⚠️ adjust path if operatorProfileStore.ts lives elsewhere
import AddPracticalMaterialModal from './parts/AddPracticalMaterialModal';
import PracticalMaterialDetailsModal from './parts/PracticalMaterialDetailsModal';
import CourseChips from '../Studymaterials/parts/CourseChips';

/* ─── Design Tokens (matches StudyMaterials.tsx) ───────────────────────── */
const C = {
  navy: '#0e225d', navyMid: '#1a3370', navyDark: '#0a1a3d',
  gold: '#fbb21f', goldDim: 'rgba(251,178,31,0.14)', goldBorder: 'rgba(251,178,31,0.35)',
  white: '#ffffff', border: '#e5e7eb', bg: '#f5f7fa', muted: '#9ca3af', sub: '#6b7280', body: '#1f2937',
  blue: '#075985', blueDim: 'rgba(56,189,248,0.14)', blueBorder: 'rgba(56,189,248,0.35)',
};

/* ─── Types (match PHP formatPracticalMaterial output) ─────────────────── */
export interface PracticalFile {
  file_name: string; key: string; url: string; size: number; mime_type: string; uploaded_at: string;
}
export interface HLSVideo {
  video_id: string; original_name: string; m3u8_url: string | null; status: string;
}
export interface PracticalMaterial {
  id: string;
  course_id: string;
  module_id: string | null;
  chapter_id: string | null;
  scope_type: 'chapter' | 'course';
  institute_id: string;
  branch_id: string | null;
  owner_level: 'institute' | 'branch';
  title: string;
  body: string;
  order: number;
  status: string;
  files: PracticalFile[];
  video_links: string[];
  videos: HLSVideo[];
  files_count: number;
  videos_count: number;
  links_count: number;
  created_by: string;
  created_by_role: 'super_admin' | 'admin' | 'operator';
  created_at: string;
}

interface Chapter {
  id: string; title: string; order: number;
  practical_materials: PracticalMaterial[];
  practical_materials_count: number;
}
interface Module {
  id: string; title: string; order: number;
  chapters: Chapter[]; chapters_count: number;
}
interface Course {
  course_id: string; name: string; title: string; status: string;
}

export interface CurrentUserContext {
  id: string;
  role: 'admin' | 'operator';
  institute_id: string;
  branch_id: string;
}

/**
 * NOTE ON THE FIX
 * ----------------
 * The original component required `currentUser`, `profileLoading`, and
 * `profileError` to always be passed in by a parent. Wherever the component
 * gets rendered as `<PracticalMaterials />` with no props (e.g. wired up
 * directly as a route/page component), TypeScript correctly flagged it:
 *
 *   Type '{}' is missing the following properties from type
 *   'PracticalMaterialsBaseProps': currentUser, profileLoading, profileError
 *
 * Fix: all three props are now optional. When they're not supplied, the
 * component pulls the profile from useOperatorProfileStore (GET
 * /v1/admin/operator-profile) instead — the same store already used
 * elsewhere in the admin panel — so it keeps working exactly as before when
 * a parent DOES pass props, and also works standalone.
 */
interface PracticalMaterialsBaseProps {
  currentUser?: CurrentUserContext | null;
  profileLoading?: boolean;
  profileError?: string | null;
}

/* ─── Permission helper ──────────────────────────────────────────────────
   super_admin's own materials: locked to everyone but super_admin (neither
   admin nor operator reach this page for those, so effectively: never).
   admin's own materials: that admin, or (n/a here) super_admin.
   operator's own materials: that operator, or the admin of the SAME
   institute+branch. */
export function canManagePracticalMaterial(material: PracticalMaterial, user: CurrentUserContext): boolean {
  if (material.created_by_role === 'super_admin') return false;
  if (material.created_by === user.id) return true;
  if (material.created_by_role === 'operator' && user.role === 'admin') {
    return user.institute_id === material.institute_id && user.branch_id === material.branch_id;
  }
  return false;
}

const PracticalMaterialsBase: React.FC<PracticalMaterialsBaseProps> = ({
  currentUser,
  profileLoading,
  profileError,
}) => {
  const toast = useRef<Toast>(null);

  /* ── Fallback for when no parent supplies profile props ────────────────
     Pulls from useOperatorProfileStore (GET /v1/admin/operator-profile)
     rather than fetching independently, so it stays in sync with whatever
     else in the app already reads/populates this store. */
  const parentControlsProfile = currentUser !== undefined || profileLoading !== undefined || profileError !== undefined;

  const { profile: operatorProfile, loading: storeLoading, error: storeError, fetchProfile } = useOperatorProfileStore();

  useEffect(() => {
    if (parentControlsProfile) return; // parent is already managing this
    if (!operatorProfile && !storeLoading && !storeError) {
      fetchProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentControlsProfile, operatorProfile, storeLoading, storeError]);

  const internalUser: CurrentUserContext | null = operatorProfile
    ? {
        id: operatorProfile.operator.user_id,
        role: operatorProfile.operator.role as 'admin' | 'operator',
        institute_id: operatorProfile.institute_id,
        branch_id: operatorProfile.branch_id,
      }
    : null;

  const resolvedUser: CurrentUserContext | null = currentUser !== undefined ? currentUser : internalUser;
  const resolvedLoading: boolean = profileLoading !== undefined ? profileLoading : storeLoading;
  const resolvedError: string | null = profileError !== undefined ? profileError : storeError;

  const [modules, setModules] = useState<Module[]>([]);
  const [generalMaterials, setGeneralMaterials] = useState<PracticalMaterial[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');

  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [expandedChapters, setExpandedChapters] = useState<Set<string>>(new Set());

  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [targetChapterId, setTargetChapterId] = useState<string | null>(null); // null = general
  const [selectedMaterial, setSelectedMaterial] = useState<PracticalMaterial | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCourseStructure = async (courseId: string) => {
    try {
      setLoading(true); setError(null);
      const response = await apiClient.get(`/v1/practical-materials/course/${courseId}/structure`);
      if (response.data.success) {
        setModules(response.data.data.modules || []);
        setGeneralMaterials(response.data.data.general_materials || []);
        const firstModule = response.data.data.modules?.[0];
        if (firstModule) {
          setExpandedModules(new Set([firstModule.id]));
          if (firstModule.chapters?.[0]) setExpandedChapters(new Set([firstModule.chapters[0].id]));
        }
      } else {
        setError('Failed to load practical materials');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load practical materials');
    } finally {
      setLoading(false);
    }
  };

  const handleCourseSelect = (course: Course) => {
    const next = course.course_id === selectedCourseId ? '' : course.course_id;
    setSelectedCourseId(next);
    if (next) fetchCourseStructure(next); else { setModules([]); setGeneralMaterials([]); }
  };

  const toggleModule  = (id: string) => setExpandedModules(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleChapter = (id: string) => setExpandedChapters(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const handleAddGeneral    = () => { setTargetChapterId(null); setSelectedMaterial(null); setShowAddModal(true); };
  const handleAddForChapter = (chapterId: string) => { setTargetChapterId(chapterId); setSelectedMaterial(null); setShowAddModal(true); };
  const handleView          = (material: PracticalMaterial) => { setSelectedMaterial(material); setShowDetailsModal(true); };
  const handleEdit          = (material: PracticalMaterial) => { setSelectedMaterial(material); setTargetChapterId(material.chapter_id); setShowAddModal(true); };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/v1/practical-materials/${deleteConfirmId}`);
      toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Practical material deleted', life: 3000 });
      setDeleteConfirmId(null);
      if (selectedCourseId) fetchCourseStructure(selectedCourseId);
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to delete', life: 4000 });
    } finally {
      setDeleting(false);
    }
  };

  if (resolvedLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '12px' }}>
        <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
        <p style={{ color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading profile data…</p>
      </div>
    );
  }

  if (resolvedError || !resolvedUser) {
    return (
      <div style={{ padding: '20px' }}>
        <Message severity="warn" text={resolvedError || 'Unable to load profile data. Please refresh the page.'} style={{ fontSize: '0.88rem' }} />
      </div>
    );
  }

  const totalMaterials = generalMaterials.length + modules.reduce((sum, m) => sum + m.chapters.reduce((s, c) => s + c.practical_materials_count, 0), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Toast ref={toast} />

      {deleteConfirmId && (
        <ConfirmDeleteBar onCancel={() => setDeleteConfirmId(null)} onConfirm={confirmDelete} deleting={deleting} />
      )}

      {/* Course Filter */}
      <div style={{ background: C.white, borderRadius: '14px', padding: '14px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '3px', height: '16px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '3px' }} />
          <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.navy, letterSpacing: '0.3px', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <i className="pi pi-filter" style={{ fontSize: '0.75rem' }} /> Select Course
          </span>
          {selectedCourseId && (
            <button
              onClick={() => { setSelectedCourseId(''); setModules([]); setGeneralMaterials([]); }}
              style={{ marginLeft: 'auto', padding: '5px 10px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', color: C.sub, fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer' }}
            >
              <i className="pi pi-times" style={{ fontSize: '0.68rem' }} /> Clear
            </button>
          )}
        </div>
        <CourseChips selectedCourseId={selectedCourseId} onCourseSelect={handleCourseSelect} showSearch={false} />
      </div>

      {error && <Message severity="error" text={error} style={{ fontSize: '0.88rem' }} />}

      {!selectedCourseId ? (
        <EmptyBlock icon="pi-book" title="Select a Course" subtitle="Click on a course above to manage its practical materials" />
      ) : loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}` }}>
          <div style={{ textAlign: 'center' }}>
            <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
            <p style={{ marginTop: '14px', color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading practical materials…</p>
          </div>
        </div>
      ) : (
        <>
          {/* Header row */}
          <div style={{ background: C.white, borderRadius: '14px', padding: '14px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '3px', height: '16px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '3px' }} />
            <span style={{ fontSize: '0.82rem', fontWeight: '700', color: C.navy }}>Practical Materials</span>
            <span style={{ fontSize: '0.75rem', color: C.muted, fontWeight: '500' }}>· {totalMaterials} total</span>
            <button
              onClick={handleAddGeneral}
              style={{ marginLeft: 'auto', padding: '7px 14px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, border: 'none', borderRadius: '9px', color: C.gold, fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <i className="pi pi-plus" style={{ fontSize: '0.7rem' }} /> Add General Material
            </button>
          </div>

          {/* General materials */}
          <div style={{ background: C.white, borderRadius: '14px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
            <SectionLabel title={`General Materials (${generalMaterials.length})`} hint="not tied to a chapter" />
            {generalMaterials.length === 0 ? (
              <p style={{ fontSize: '0.8rem', color: C.muted, margin: 0 }}>None yet for this course.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {generalMaterials.map((m) => (
                  <MaterialCard key={m.id} material={m} positionLabel={`G.${m.order}`} currentUser={resolvedUser}
                    onView={() => handleView(m)} onEdit={() => handleEdit(m)} onDelete={() => setDeleteConfirmId(m.id)} />
                ))}
              </div>
            )}
          </div>

          {/* Module > Chapter tree */}
          {modules.length === 0 ? (
            <EmptyBlock icon="pi-sitemap" title="No modules found" subtitle="Ask a super admin to set up modules/chapters for this course" />
          ) : (
            <div style={{ background: C.white, borderRadius: '14px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
              {modules.map((module) => (
                <div key={module.id} style={{ marginBottom: '10px' }}>
                  <button
                    onClick={() => toggleModule(module.id)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', cursor: 'pointer', textAlign: 'left' }}
                  >
                    <i className={`pi ${expandedModules.has(module.id) ? 'pi-chevron-down' : 'pi-chevron-right'}`} style={{ fontSize: '0.7rem', color: C.navy }} />
                    <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: '700', color: C.gold, flexShrink: 0 }}>
                      {module.order}
                    </div>
                    <span style={{ fontSize: '0.88rem', fontWeight: '700', color: C.navy }}>{module.title}</span>
                    <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: C.muted }}>{module.chapters_count} chapter{module.chapters_count !== 1 ? 's' : ''}</span>
                  </button>

                  {expandedModules.has(module.id) && (
                    <div style={{ paddingLeft: '30px', marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {module.chapters.map((chapter) => (
                        <div key={chapter.id} style={{ border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', background: C.white }}>
                            <button onClick={() => toggleChapter(chapter.id)} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', cursor: 'pointer', flex: 1, textAlign: 'left', padding: 0 }}>
                              <i className={`pi ${expandedChapters.has(chapter.id) ? 'pi-chevron-down' : 'pi-chevron-right'}`} style={{ fontSize: '0.65rem', color: C.navy }} />
                              <span style={{ fontSize: '0.82rem', fontWeight: '600', color: C.navy }}>{module.order}.{chapter.order} {chapter.title}</span>
                              <span style={{ fontSize: '0.7rem', color: C.muted }}>({chapter.practical_materials_count})</span>
                            </button>
                            <button
                              onClick={() => handleAddForChapter(chapter.id)}
                              style={{ padding: '4px 10px', background: C.blueDim, border: `1px solid ${C.blueBorder}`, borderRadius: '7px', color: C.blue, fontSize: '0.7rem', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}
                            >
                              <i className="pi pi-plus" style={{ marginRight: '4px', fontSize: '0.62rem' }} /> Add
                            </button>
                          </div>

                          {expandedChapters.has(chapter.id) && (
                            <div style={{ borderTop: `1px solid ${C.border}`, padding: '10px', background: C.bg }}>
                              {chapter.practical_materials.length === 0 ? (
                                <p style={{ margin: 0, fontSize: '0.76rem', color: C.muted, textAlign: 'center', padding: '10px 0' }}>No practical materials in this chapter</p>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                                  {chapter.practical_materials.map((m) => (
                                    <MaterialCard key={m.id} material={m} positionLabel={`${module.order}.${chapter.order}.${m.order}`} currentUser={resolvedUser}
                                      onView={() => handleView(m)} onEdit={() => handleEdit(m)} onDelete={() => setDeleteConfirmId(m.id)} />
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {selectedMaterial && (
        <PracticalMaterialDetailsModal
          visible={showDetailsModal}
          material={selectedMaterial}
          canManage={canManagePracticalMaterial(selectedMaterial, resolvedUser)}
          onHide={() => setShowDetailsModal(false)}
          onEdit={() => { setShowDetailsModal(false); setTargetChapterId(selectedMaterial.chapter_id); setShowAddModal(true); }}
        />
      )}

      <AddPracticalMaterialModal
        visible={showAddModal}
        courseId={selectedCourseId}
        chapterId={targetChapterId}
        editingMaterial={selectedMaterial}
        onHide={() => { setShowAddModal(false); setSelectedMaterial(null); setTargetChapterId(null); }}
        onSuccess={() => {
          toast.current?.show({ severity: 'success', summary: 'Success', detail: selectedMaterial ? 'Practical material updated' : 'Practical material created', life: 3000 });
          setShowAddModal(false); setSelectedMaterial(null); setTargetChapterId(null);
          if (selectedCourseId) fetchCourseStructure(selectedCourseId);
        }}
      />
    </div>
  );
};

/* ─── Sub-components ─────────────────────────────────────────────────── */

const EmptyBlock: React.FC<{ icon: string; title: string; subtitle: string }> = ({ icon, title, subtitle }) => (
  <div style={{ padding: '60px 20px', textAlign: 'center', background: C.white, borderRadius: '14px', border: `2px dashed ${C.border}` }}>
    <div style={{ width: '48px', height: '48px', background: 'rgba(14,34,93,0.06)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
      <i className={`pi ${icon}`} style={{ fontSize: '1.4rem', color: C.navy, opacity: 0.3 }} />
    </div>
    <h3 style={{ margin: '0 0 6px 0', color: C.navy, fontSize: '0.95rem', fontWeight: '700' }}>{title}</h3>
    <p style={{ margin: 0, color: C.muted, fontSize: '0.83rem' }}>{subtitle}</p>
  </div>
);

const SectionLabel: React.FC<{ title: string; hint?: string }> = ({ title, hint }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
    <div style={{ width: '3px', height: '14px', background: `linear-gradient(180deg, ${C.gold} 0%, #e09a00 100%)`, borderRadius: '3px' }} />
    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: C.navy }}>{title}</span>
    {hint && <span style={{ fontSize: '0.72rem', color: C.muted }}>— {hint}</span>}
  </div>
);

const OwnerBadge: React.FC<{ material: PracticalMaterial }> = ({ material }) => {
  const isInstitute = material.owner_level === 'institute';
  return (
    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '0.62rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px', background: isInstitute ? C.goldDim : C.blueDim, color: isInstitute ? '#92580a' : C.blue, border: `1px solid ${isInstitute ? C.goldBorder : C.blueBorder}` }}>
      {isInstitute ? 'Institute' : 'Branch'}
    </span>
  );
};

const MaterialCard: React.FC<{
  material: PracticalMaterial; positionLabel: string; currentUser: CurrentUserContext;
  onView: () => void; onEdit: () => void; onDelete: () => void;
}> = ({ material, positionLabel, currentUser, onView, onEdit, onDelete }) => {
  const manageable = canManagePracticalMaterial(material, currentUser);
  const readyVideos = material.videos?.filter(v => v.status === 'ready').length ?? 0;

  return (
    <div style={{ padding: '10px 12px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '9px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <div onClick={onView} style={{ width: '30px', height: '30px', borderRadius: '8px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.55rem', fontWeight: '700', color: C.gold, cursor: 'pointer' }}>
          {positionLabel}
        </div>
        <div onClick={onView} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <h5 style={{ margin: 0, fontSize: '0.83rem', fontWeight: '600', color: C.navy }}>{material.title}</h5>
            <OwnerBadge material={material} />
            {material.status !== 'active' && (
              <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '0.62rem', fontWeight: '700', textTransform: 'capitalize', background: '#f3f4f6', color: C.sub, border: `1px solid ${C.border}` }}>
                {material.status}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '0.67rem', color: C.muted }}>
            {material.files_count > 0 && <span><i className="pi pi-paperclip" style={{ marginRight: '3px' }} />{material.files_count} file{material.files_count !== 1 ? 's' : ''}</span>}
            {readyVideos > 0 && <span style={{ color: '#16a34a', fontWeight: '600' }}><i className="pi pi-play-circle" style={{ marginRight: '3px' }} />{readyVideos} video{readyVideos !== 1 ? 's' : ''}</span>}
          </div>
        </div>
        {manageable && (
          <div style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
            <button onClick={onEdit} title="Edit" style={{ width: '26px', height: '26px', background: 'rgba(14,34,93,0.06)', border: 'none', borderRadius: '6px', color: C.navy, cursor: 'pointer' }}>
              <i className="pi pi-pencil" style={{ fontSize: '0.65rem' }} />
            </button>
            <button onClick={onDelete} title="Delete" style={{ width: '26px', height: '26px', background: 'rgba(220,38,38,0.08)', border: 'none', borderRadius: '6px', color: '#dc2626', cursor: 'pointer' }}>
              <i className="pi pi-trash" style={{ fontSize: '0.65rem' }} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ConfirmDeleteBar: React.FC<{ onCancel: () => void; onConfirm: () => void; deleting: boolean }> = ({ onCancel, onConfirm, deleting }) => (
  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
    <i className="pi pi-exclamation-triangle" style={{ color: '#dc2626' }} />
    <span style={{ fontSize: '0.82rem', color: '#991b1b', fontWeight: '600', flex: 1 }}>Delete this practical material? This cannot be undone.</span>
    <button onClick={onCancel} disabled={deleting} style={{ padding: '6px 12px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '7px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
    <button onClick={onConfirm} disabled={deleting} style={{ padding: '6px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '7px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer' }}>
      {deleting ? 'Deleting…' : 'Delete'}
    </button>
  </div>
);

export default PracticalMaterialsBase;